import React from 'react';

type AppLoadingLayout = 'cards' | 'rows' | 'table';
type AppLoadingDensity = 'page' | 'section';

type Props = {
  title: string;
  message?: string;
  hint?: string;
  layout?: AppLoadingLayout;
  density?: AppLoadingDensity;
};

const BASE_SURFACE: React.CSSProperties = {
  border: '1px solid #dbe2ea',
  borderRadius: '0.95rem',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
  display: 'grid',
  gap: '1rem'
};

const SKELETON_BLOCK: React.CSSProperties = {
  borderRadius: '0.7rem',
  background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
  border: '1px solid #e5e7eb'
};

function renderCards() {
  return (
    <div
      style={{
        display: 'grid',
        gap: '0.85rem',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))'
      }}
    >
      {[0, 1, 2].map(index => (
        <div
          key={index}
          style={{
            ...SKELETON_BLOCK,
            padding: '1rem',
            minHeight: '140px',
            display: 'grid',
            gap: '0.65rem',
            alignContent: 'start'
          }}
        >
          <div style={{ ...SKELETON_BLOCK, height: '1rem', width: index === 1 ? '58%' : '46%' }} />
          <div style={{ ...SKELETON_BLOCK, height: '2rem', width: index === 2 ? '28%' : '36%' }} />
          <div style={{ ...SKELETON_BLOCK, height: '0.9rem', width: '92%' }} />
          <div style={{ ...SKELETON_BLOCK, height: '0.9rem', width: '76%' }} />
          <div style={{ ...SKELETON_BLOCK, height: '2.2rem', width: '42%', borderRadius: '999px' }} />
        </div>
      ))}
    </div>
  );
}

function renderRows() {
  return (
    <div style={{ display: 'grid', gap: '0.7rem' }}>
      {[0, 1, 2, 3].map(index => (
        <div
          key={index}
          style={{
            ...SKELETON_BLOCK,
            padding: '0.95rem 1rem',
            display: 'grid',
            gap: '0.5rem'
          }}
        >
          <div style={{ ...SKELETON_BLOCK, height: '1rem', width: index % 2 === 0 ? '32%' : '24%' }} />
          <div style={{ ...SKELETON_BLOCK, height: '0.9rem', width: '88%' }} />
        </div>
      ))}
    </div>
  );
}

function renderTable() {
  return (
    <div style={{ display: 'grid', gap: '0.8rem' }}>
      <div
        style={{
          display: 'grid',
          gap: '0.6rem',
          gridTemplateColumns: '1.2fr 1fr 0.9fr 0.8fr'
        }}
      >
        {[0, 1, 2, 3].map(index => (
          <div key={index} style={{ ...SKELETON_BLOCK, height: '1rem', width: '100%' }} />
        ))}
      </div>
      {[0, 1, 2, 3, 4].map(index => (
        <div
          key={index}
          style={{
            ...SKELETON_BLOCK,
            padding: '0.9rem 1rem',
            display: 'grid',
            gap: '0.6rem',
            gridTemplateColumns: '1.2fr 1fr 0.9fr 0.8fr',
            alignItems: 'center'
          }}
        >
          {[0, 1, 2, 3].map(cellIndex => (
            <div
              key={cellIndex}
              style={{
                ...SKELETON_BLOCK,
                height: '0.95rem',
                width: cellIndex === 0 ? '80%' : cellIndex === 3 ? '44%' : '62%'
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function renderPreview(layout: AppLoadingLayout) {
  if (layout === 'rows') return renderRows();
  if (layout === 'table') return renderTable();
  return renderCards();
}

export function AppLoadingState({
  title,
  message = 'Please keep this tab open while the workspace finishes loading.',
  hint,
  layout = 'cards',
  density = 'page'
}: Props) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        ...BASE_SURFACE,
        padding: density === 'page' ? '1.25rem 1.35rem' : '1rem 1.1rem'
      }}
    >
      <div style={{ display: 'grid', gap: '0.45rem' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.45rem',
            fontSize: '0.78rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 700,
            color: '#1d4ed8'
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: '0.55rem',
              height: '0.55rem',
              borderRadius: '999px',
              backgroundColor: '#2563eb',
              boxShadow: '0 0 0 6px rgba(37, 99, 235, 0.12)'
            }}
          />
          Loading
        </div>
        <div style={{ fontSize: density === 'page' ? '1.15rem' : '1rem', fontWeight: 700, color: '#0f172a' }}>
          {title}
        </div>
        <div style={{ color: '#475569', fontSize: '0.92rem', maxWidth: '760px' }}>{message}</div>
        {hint ? <div style={{ color: '#64748b', fontSize: '0.84rem' }}>{hint}</div> : null}
      </div>

      {renderPreview(layout)}
    </div>
  );
}

export default AppLoadingState;
